import { ColumnDef } from '@tanstack/react-table';
import { useEngagementPermissions } from '@anytag/auth';
import { Button, TableCell } from '@anytag/shared-atoms';
import { formatPercent, formatIntNumber } from '@anytag/shared-utils';
import { useTranslateCountry, useQueryHelper } from '@anytag/shared-hooks';
import {
  EngagementCampaignSearchSelectInfluencers,
  useEngagementAddInfluencersToProposalFromAnotherCampaignMutation
} from '@anytag/graphql';

interface Props {
  campaignId?: number;
}

export const useCampaignInfluencersTable = ({ campaignId }: Props) => {
  const { tc } = useTranslateCountry();
  const { t, enqueueSnackbar } = useQueryHelper();
  const { hideAddPackageToProposal } = useEngagementPermissions();
  const { callEngagementAddInfluencersToProposalFromAnotherCampaign, loading: addingProposal } =
    useEngagementAddInfluencersToProposalFromAnotherCampaignMutation();

  const handleAddCampaignInfluencersToProposal = async (fromCampaignId: number, name: string) => {
    if (!campaignId) {
      return;
    }

    try {
      await callEngagementAddInfluencersToProposalFromAnotherCampaign({
        variables: { input: { fromCampaignId, toCampaignId: campaignId } }
      });
      enqueueSnackbar(t('influencers from the campaign were added to the proposal', { name }), {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  const columns: ColumnDef<EngagementCampaignSearchSelectInfluencers>[] = [
    {
      accessorKey: 'name',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Package'),
      cell: ({ row }) => {
        const { id, name } = row.original;

        return (
          <div css={{ display: 'flex', justifyContent: 'space-between', minWidth: '350px' }}>
            <TableCell.Link label={name} target="_blank" to="/engagement/$id" params={{ id: id.toString() }} />
            {!hideAddPackageToProposal ? (
              <Button
                variant="solid"
                loading={addingProposal}
                disabled={addingProposal}
                suffixIcon={{ icon: 'plus', size: '10px' }}
                onClick={() => handleAddCampaignInfluencersToProposal(id, name)}
                className="h-6 w-6 min-w-[unset] p-0"
                tooltip={{ disabled: addingProposal, help: t("Button.Add campaign's influencers to the proposal") }}
              />
            ) : null}
          </div>
        );
      }
    },
    {
      accessorKey: 'category',
      header: t<string>('HeaderColumn.Campaign Category'),
      cell: ({ row }) => <TableCell.Text value={t(`Option.${row.original.category}`)} />
    },
    {
      accessorKey: 'countries',
      header: t<string>('HeaderColumn.Country'),
      cell: ({ row }) => {
        const { countries } = row.original;

        return (
          <TableCell.Text
            value={countries.length > 0 ? countries.map((country) => tc(country)).join(', ') : '-'}
            tooltip={{
              help: countries?.length ? (
                <ul>{countries?.map((country) => <li key={country}>{tc(country)}</li>)}</ul>
              ) : null
            }}
          />
        );
      }
    },
    {
      accessorKey: 'engagementRate',
      header: t<string>('HeaderColumn.ER'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number className="min-w-0" value={formatPercent(row.original.engagementRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'numberOfInfluencers',
      header: t<string>('HeaderColumn.Number of influencers'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number className="min-w-0" value={formatIntNumber(row.original.numberOfInfluencers)} />
      )
    }
  ];

  return { columns };
};
